import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { RichTextEditor } from '@/features/estimate/components/RichTextEditor';
import { sanitizeHtml } from '@/lib/sanitize-html';
import type {
  AssumptionsOverrideHandler,
  DetailsMutatedHandler,
  DetailsTableHandler,
  GenerateSummaryHandler,
  LineItemDeleteHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
  ProjectNameHandler,
  SummaryOverrideHandler,
  TimelineHandler,
} from '../types';
import { capabilitiesForMode } from '../types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScopeStatementsTable } from './ScopeStatementsTable';
import { SectionHeading } from './SectionHeading';
import {
  SECTION_II_HEADER_ACTION_BUTTON_CLASS,
  SECTION_II_LABEL_CLASS,
  SectionIIHeaderRow,
  SectionIILabeledRule,
} from './SectionIIHeaderRow';
import { AddSkuInline } from '@/features/estimate/components/AddSkuInline';
import { AddOtherInline } from '@/features/estimate/components/AddOtherInline';
import { formatAnswerDisplay, formatCurrency } from '@/features/estimate/lib/format';
import { cn } from '@/lib/utils';
import { formatPaperFieldLabel, formatPaperFieldValue } from '@/lib/paper-display';
import { estimateApi } from '@/services/estimate';
import type { EstimateLineItem } from '@/types/estimate';

/**
 * Sprint 41 §14.S10 — Section II "Project Summary".
 *
 * Wave 18 enrichment: 5-group Project Details accordion per
 * §14.S10-Q5 lock. Until the backend adds `ProjectDetail.groupCode`
 * (deferred per IMPLEMENTATION-STATUS-2026-05-09.md cleanup wave),
 * this component groups frontend-side via a heuristic on the detail
 * `key` field. Once the backend column lands, the grouping switches
 * to read from the snapshot directly.
 *
 * Locked groups:
 *   SITE_CONDITIONS, PERMITS_COMPLIANCE, SCHEDULE_LOGISTICS,
 *   CUSTOMER_PREFERENCES, SPECIAL_REQUIREMENTS
 *
 * Editability per `mode` capability — `isEditable` enables inline
 * rich-text editing (TODO subsequent wave).
 */
export interface SectionII_ProjectSummaryProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
  /**
   * W32 enrichment Slice 1 — when supplied AND `caps.isEditable`,
   * renders the Timeline editor (weeksUntilStart / weeksToComplete /
   * tentativeStartDate). When omitted, the timeline still renders as a
   * read-only line if any of the three fields is set.
   */
  onTimeline?: TimelineHandler;
  /**
   * W32 enrichment Slice 2 — when supplied AND `caps.isEditable`,
   * renders the Assumptions rich-text editor + reset link. Reads
   * from `project.assumptionsOverride`; pass null on save to clear
   * the override.
   */
  onAssumptionsOverride?: AssumptionsOverrideHandler;
  /**
   * W32 enrichment Slice 3 — when supplied AND `caps.isEditable`,
   * renders a "Generate with AI" button next to the Narrative editor.
   * The handler calls the backend AI summary endpoint AND refreshes
   * the page's layout data so the new narrative lands in the same box.
   * Never invoked on mount.
   */
  onGenerateSummary?: GenerateSummaryHandler;
  /**
   * Founder 2026-09-01 — persist the estimator-authored Narrative.
   * When supplied AND `caps.isEditable`, Section II shows the empty
   * rich-text box (with toolbar) so the user can write or generate.
   */
  onSaveSummary?: SummaryOverrideHandler;
  onSaveProjectName?: ProjectNameHandler;
  /**
   * W32 enrichment Slice 4 (Wave 4e) — when supplied AND
   * `caps.isEditable`, renders inline edit controls on each
   * DetailGroup row. Persists via
   * PATCH /estimate/details/:detailId/value.
   */
  onDetailValue?: DetailsTableHandler;
  /**
   * Reload resolve payload after material/service costs are attached
   * to a Project Detail row (POST /estimate/details/:id/line-items).
   */
  onDetailsMutated?: DetailsMutatedHandler;
  /** Delete a material/service cost line on a Project Detail row. */
  onLineItemDelete?: LineItemDeleteHandler;
}

interface ProjectDetail {
  id: string;
  key: string;
  label: string;
  value?: unknown;
  displayOrder?: number;
  groupCode?: ProjectDetailGroupCode;
  /** When false, the row is informational only — no cost affordance. */
  allowsLineItems?: boolean;
  lineItems?: EstimateLineItem[];
  rowTotal?: string;
}

/**
 * Sprint 58 project-level Scope Notes block (`resolved.projectScope`)
 * — drives the founder-mockup "Project Detail" cards grid (premises,
 * project type, site conditions, access, working hours, …). Loose
 * mirror of EstimateProjectScopeDto.
 */
interface ProjectScopeBlock {
  fields?: Array<{
    id: string;
    scfCode: string;
    scfLabel: string;
    contractLabel?: string | null;
    groupLabel?: string | null;
    answerValue?: unknown;
    visibilityResolved?: boolean;
    displayOrder?: number;
  }>;
  statements?: Array<{
    scfCode: string;
    groupLabel?: string | null;
    text: string;
    source?: string;
  }>;
}

type ProjectDetailGroupCode =
  | 'SITE_CONDITIONS'
  | 'PERMITS_COMPLIANCE'
  | 'SCHEDULE_LOGISTICS'
  | 'CUSTOMER_PREFERENCES'
  | 'SPECIAL_REQUIREMENTS';

const GROUP_ORDER: ProjectDetailGroupCode[] = [
  'SITE_CONDITIONS',
  'PERMITS_COMPLIANCE',
  'SCHEDULE_LOGISTICS',
  'CUSTOMER_PREFERENCES',
  'SPECIAL_REQUIREMENTS',
];

const GROUP_LABEL: Record<ProjectDetailGroupCode, string> = {
  SITE_CONDITIONS: 'Site Conditions',
  PERMITS_COMPLIANCE: 'Permits & Compliance',
  SCHEDULE_LOGISTICS: 'Schedule & Logistics',
  CUSTOMER_PREFERENCES: 'Customer Preferences',
  SPECIAL_REQUIREMENTS: 'Special Requirements',
};

/**
 * Heuristic group-by-key fallback used until backend adds explicit
 * `ProjectDetail.groupCode`. Matches keyword fragments in the
 * detail's `key` field.
 */
const KEY_PATTERNS: Array<[ProjectDetailGroupCode, RegExp[]]> = [
  [
    'SITE_CONDITIONS',
    [
      /site/i,
      /access/i,
      /terrain/i,
      /existing/i,
      /utility/i,
      /jobsite/i,
      /obstruction/i,
      /demolition/i,
      /driveway/i,
      /slope/i,
    ],
  ],
  ['PERMITS_COMPLIANCE', [/permit/i, /hoa/i, /code/i, /easement/i, /compliance/i]],
  ['SCHEDULE_LOGISTICS', [/schedule/i, /timeline/i, /start/i, /duration/i, /weather/i, /crew/i, /delivery/i]],
  ['CUSTOMER_PREFERENCES', [/preference/i, /color/i, /finish/i, /brand/i, /style/i, /design/i]],
  ['SPECIAL_REQUIREMENTS', [/special/i, /accessibility/i, /pet/i, /child/i, /sourcing/i, /sustainability/i, /requirement/i, /rendering/i]],
];

function groupForDetail(detail: ProjectDetail): ProjectDetailGroupCode {
  if (detail.groupCode) return detail.groupCode;
  const haystack = `${detail.key} ${detail.label}`;
  for (const [code, patterns] of KEY_PATTERNS) {
    if (patterns.some((p) => p.test(haystack))) return code;
  }
  return 'SPECIAL_REQUIREMENTS';
}

function isRequiredApprovals(detail: ProjectDetail): boolean {
  const haystack = `${detail.key} ${detail.label}`;
  return /required\s*approvals/i.test(haystack) || /requiredApprovals/i.test(detail.key);
}

export function SectionII_ProjectSummary({
  data,
  mode,
  onTimeline,
  onAssumptionsOverride,
  onGenerateSummary,
  onSaveSummary,
  onSaveProjectName,
  onDetailValue,
  onDetailsMutated,
  onLineItemDelete,
}: SectionII_ProjectSummaryProps) {
  const caps = capabilitiesForMode(mode);
  const details = (data.projectDetails as ProjectDetail[] | undefined) ?? [];
  const project = data.project as Record<string, unknown>;
  const assumptionsOverride =
    (project.assumptionsOverride as string | undefined | null) ?? null;
  const assumptionsDefault =
    (typeof project.assumptionsDefault === 'string' &&
      project.assumptionsDefault.trim()) ||
    null;
  const assumptions = assumptionsOverride ?? assumptionsDefault;
  const persistedSummary =
    typeof project.summary === 'string' ? project.summary : null;

  const administrationDetail = useMemo(
    () => details.find((d) => d.key === 'administration') ?? null,
    [details],
  );

  const requiredApprovalsDetails = useMemo(
    () => details.filter((d) => d.key !== 'administration' && isRequiredApprovals(d)),
    [details],
  );

  const grouped = useMemo(() => {
    const map = new Map<ProjectDetailGroupCode, ProjectDetail[]>();
    for (const code of GROUP_ORDER) map.set(code, []);
    for (const d of details) {
      if (d.key === 'administration') continue;
      if (isRequiredApprovals(d)) continue;
      const code = groupForDetail(d);
      map.get(code)!.push(d);
    }
    // Sort within each group by displayOrder.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    }
    return map;
  }, [details]);

  // Founder 2026-07-29 — Project Details need a section subtotal like
  // each scope element footer, so permit / site costs aren't invisible
  // outside the accordion rows.
  const projectDetailsSubtotal = useMemo(() => {
    return details.reduce((sum, d) => {
      const n = Number(d.rowTotal ?? 0);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
  }, [details]);

  const projectScope = (data.projectScope as ProjectScopeBlock | null | undefined) ?? null;

  return (
    <section
      id="section-2"
      className="scroll-mt-24"
      data-mode={mode}
      data-section="II"
    >
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <SectionHeading numeral="II." title="Project Summary" className="px-6 pt-5 pb-1" />
        <p className="px-6 pb-4 font-public-sans text-xs text-muted-foreground">
          Scope-of-work narrative, timeline, key metrics, and project detail.
        </p>

        <div className="space-y-4 px-6 pb-6">
        <ProjectNameBlock
          name={data.project.projectName ?? ''}
          editable={caps.isEditable && !!onSaveProjectName}
          onSave={onSaveProjectName}
        />

        {/* Narrative — founder 2026-09-01: empty rich-text box first,
            optional Generate with AI. Never auto-fills on mount.
            Header is SectionIIHeaderRow so it stays aligned with
            Timeline / Assumptions (FE #292). */}
        <NarrativeBlock
          summary={persistedSummary}
          editable={caps.isEditable && (!!onSaveSummary || !!onGenerateSummary)}
          onGenerate={onGenerateSummary}
          onSave={onSaveSummary}
        />

        {/* Founder mockup 2026-07-16 — key-metrics row (est. start /
            est. completion / duration). Only metrics the project
            actually carries render. CSF-R4 (2026-07-18): the mockup's
            SQUARE FOOTAGE tile now renders from the resolver's
            `projectSquareFootage` (sum of the included elements'
            primary area fields) when non-null. */}
        {/* Founder bug 5 round 2 (2026-07-31) — the metrics band IS the
            timeline surface. The separate TIMELINE row duplicated the
            same fields; editing now lives on this band's Edit dialog. */}
        <MetricsRow
          project={project}
          squareFootage={toNumberOrNull(data.projectSquareFootage)}
          editable={caps.isEditable && !!onTimeline}
          onTimeline={onTimeline}
        />

        {/* Assumptions — W32 enrichment Slice 2. Editor when
            caps.isEditable + handler supplied; otherwise read-only
            HTML body. Nothing rendered when override is null in
            read-only modes. */}
        <AssumptionsBlock
          assumptions={assumptions}
          isOverride={assumptionsOverride != null}
          editable={caps.isEditable && !!onAssumptionsOverride}
          onSave={onAssumptionsOverride}
        />

        {/* Founder mockup 2026-07-16 — "Project Detail" cards grid
            populated from the project-level Scope Notes block
            (resolved.projectScope statements: premises, project type,
            site conditions, access, working hours, …). Renders only
            when the project has a __PROJECT_SCOPE__ submission. */}
        <ProjectDetailCards projectScope={projectScope} />

        {/* 5-group Project Detail accordions. Task 18: Required
            Approvals last among the groups, Administration last of all
            and estimator-only. */}
        {(details.some(
          (d) => d.key !== 'administration' && !isRequiredApprovals(d),
        ) ||
          requiredApprovalsDetails.length > 0) && (
          <div
            className="space-y-2 pt-4"
            data-testid="section-ii-project-details"
          >
            <SectionIILabeledRule label="Project Details" muted />
            {GROUP_ORDER.map((code) => {
              const items = grouped.get(code) ?? [];
              if (items.length === 0) return null;
              return (
                <DetailGroup
                  key={code}
                  label={GROUP_LABEL[code]}
                  items={items}
                  editable={caps.isEditable && !!onDetailValue}
                  onSave={onDetailValue}
                  showCosts={caps.showCostBreakdown}
                  costsEditable={caps.isEditable && caps.showCostBreakdown}
                  onDetailsMutated={onDetailsMutated}
                  onLineItemDelete={
                    caps.isEditable && caps.showCostBreakdown
                      ? onLineItemDelete
                      : undefined
                  }
                />
              );
            })}
            {requiredApprovalsDetails.length > 0 && (
              <DetailGroup
                label="Required Approvals"
                items={requiredApprovalsDetails}
                editable={caps.isEditable && !!onDetailValue}
                onSave={onDetailValue}
                showCosts={caps.showCostBreakdown}
                costsEditable={caps.isEditable && caps.showCostBreakdown}
                onDetailsMutated={onDetailsMutated}
                onLineItemDelete={
                  caps.isEditable && caps.showCostBreakdown
                    ? onLineItemDelete
                    : undefined
                }
              />
            )}
            {caps.showCostBreakdown && projectDetailsSubtotal > 0 && (
              <div
                className="mt-3 rounded-md bg-muted/30 px-4 py-3"
                data-testid="project-details-subtotal"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-public-sans text-sm font-bold text-foreground">
                    Project Details subtotal
                  </span>
                  <span className="whitespace-nowrap font-mono text-sm font-bold text-foreground">
                    {formatCurrency(String(projectDetailsSubtotal))}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {administrationDetail && caps.showCostBreakdown && (
          <div
            className="space-y-3 pt-2"
            data-testid="section-ii-administration-costs"
          >
            <SectionIILabeledRule label="Administration" />
            <div
              className="rounded-r-md border-l-4 border-l-primary bg-muted/40 px-3.5 py-3"
              data-testid="section-ii-administration-inset"
            >
              <DetailRow
                detail={administrationDetail}
                editable={false}
                onSave={undefined}
                showCosts={caps.showCostBreakdown}
                costsEditable={caps.isEditable && caps.showCostBreakdown}
                onDetailsMutated={onDetailsMutated}
                onLineItemDelete={
                  caps.isEditable && caps.showCostBreakdown
                    ? onLineItemDelete
                    : undefined
                }
                allowOther
              />
            </div>
          </div>
        )}

        {/* Screenshot defect #2 (2026-07-23) — the old "DetailsTable
            inline editing is available when an onDetailValue handler…"
            developer note rendered to end users. Removed: absent
            handlers simply render the rows read-only. */}
        </div>
      </div>
    </section>
  );
}

/**
 * Founder mockup 2026-07-16 — Section II key-metrics row. Renders one
 * tile per metric the project actually carries:
 *
 *   EST. START      — tentativeStartDate (fallback estimatedStartDate)
 *   EST. COMPLETION — start date + weeksToComplete
 *   DURATION        — "~ N weeks" (weeksToComplete) or
 *                     "~ N days" (estimatedDurationDays)
 *   SQUARE FOOTAGE  — resolver `projectSquareFootage` (CSF-R4: sum of
 *                     the included elements' primary area fields)
 *
 * Renders nothing when no metric exists.
 */
function MetricsRow({
  project,
  squareFootage,
  editable = false,
  onTimeline,
}: {
  project: Record<string, unknown>;
  squareFootage?: number | null;
  /** Founder bug 5 round 2 — this band owns timeline editing. */
  editable?: boolean;
  onTimeline?: TimelineHandler;
}) {
  const startRaw =
    (project.tentativeStartDate as string | null | undefined) ??
    (project.estimatedStartDate as string | null | undefined) ??
    null;
  // Date-only strings parse as LOCAL midnight — `new Date('YYYY-MM-DD')`
  // is UTC midnight and rendered a day off next to the old timeline row
  // (founder screenshot: Aug 15 vs Aug 14).
  const startDate = parseDateOnlyLocal(startRaw);
  const validStart = startDate != null && !Number.isNaN(startDate.getTime());
  const weeksToComplete = toNumberOrNull(project.weeksToComplete);
  const durationDays = toNumberOrNull(project.estimatedDurationDays);
  const weeksUntilStart = toNumberOrNull(project.weeksUntilStart);

  // Timeline edit dialog state (moved here from the deleted TimelineBlock).
  const initialTentative = toDateInputValue(project.tentativeStartDate);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [weeksStartInput, setWeeksStartInput] = useState<string>(
    weeksUntilStart == null ? '' : String(weeksUntilStart),
  );
  const [weeksCompleteInput, setWeeksCompleteInput] = useState<string>(
    weeksToComplete == null ? '' : String(weeksToComplete),
  );
  const [tentativeInput, setTentativeInput] = useState<string>(initialTentative);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setWeeksStartInput(weeksUntilStart == null ? '' : String(weeksUntilStart));
    setWeeksCompleteInput(
      weeksToComplete == null ? '' : String(weeksToComplete),
    );
    setTentativeInput(initialTentative);
    // intentionally derived
  }, [weeksUntilStart, weeksToComplete, initialTentative]);

  const handleSave = async (): Promise<boolean> => {
    if (!onTimeline) return false;
    setSaving(true);
    setError(null);
    try {
      await onTimeline({
        weeksUntilStart:
          weeksStartInput === ''
            ? null
            : Math.max(0, parseInt(weeksStartInput, 10) || 0),
        weeksToComplete:
          weeksCompleteInput === ''
            ? null
            : Math.max(0, parseInt(weeksCompleteInput, 10) || 0),
        tentativeStartDate: tentativeInput === '' ? null : tentativeInput,
      });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save timeline');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const completionDate =
    validStart && weeksToComplete != null
      ? new Date(startDate!.getTime() + weeksToComplete * 7 * 24 * 60 * 60 * 1000)
      : validStart && durationDays != null
        ? new Date(startDate!.getTime() + durationDays * 24 * 60 * 60 * 1000)
        : null;

  const tiles: Array<{ label: string; value: string }> = [];
  if (validStart) {
    tiles.push({ label: 'Est. start', value: formatMetricDate(startDate!) });
  } else if (weeksUntilStart != null) {
    // No date picked yet — the weeks-out figure still communicates the
    // start window (was only on the deleted TIMELINE row).
    tiles.push({
      label: 'Est. start',
      value: `~ ${weeksUntilStart} ${weeksUntilStart === 1 ? 'week' : 'weeks'} out`,
    });
  }
  if (completionDate) {
    tiles.push({ label: 'Est. completion', value: formatMetricDate(completionDate) });
  }
  if (weeksToComplete != null) {
    tiles.push({
      label: 'Duration',
      value: `~ ${weeksToComplete} ${weeksToComplete === 1 ? 'week' : 'weeks'}`,
    });
  } else if (durationDays != null) {
    tiles.push({
      label: 'Duration',
      value: `~ ${durationDays} ${durationDays === 1 ? 'day' : 'days'}`,
    });
  }
  if (squareFootage != null) {
    tiles.push({
      label: 'Square footage',
      value: `${squareFootage.toLocaleString('en-US')} sf`,
    });
  }

  const canEdit = editable && onTimeline != null;
  if (tiles.length === 0 && !canEdit) return null;

  return (
    <div
      className="space-y-2"
      data-testid="section-ii-metrics"
    >
      <SectionIIHeaderRow
        label="Timeline"
        action={
          canEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                SECTION_II_HEADER_ACTION_BUTTON_CLASS,
                'text-primary hover:text-primary-dim',
              )}
              data-testid="timeline-edit"
              onClick={() => setDialogOpen(true)}
            >
              {tiles.length > 0 ? 'Edit' : 'Add timeline'}
            </Button>
          ) : undefined
        }
      />
      {tiles.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/30 px-4 py-3 md:grid-cols-4">
          {tiles.map((t) => (
            <div key={t.label}>
              <p className="font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                {t.label}
              </p>
              <p className="mt-1 font-newsreader text-base font-semibold text-foreground">
                {t.value}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="font-public-sans text-sm italic text-muted-foreground">
          No timeline yet — add est. start and duration.
        </p>
      )}

      {canEdit && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Update timeline</DialogTitle>
              <DialogDescription>
                These values print on the contract's Project Summary.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <span className="block text-xs text-foreground-soft">Weeks until start</span>
                <Input
                  type="number"
                  min={0}
                  value={weeksStartInput}
                  onChange={(e) => setWeeksStartInput(e.target.value)}
                  placeholder="—"
                  className="mt-1 w-full"
                  data-testid="timeline-weeks-until-start"
                />
              </div>
              <div>
                <span className="block text-xs text-foreground-soft">Weeks to complete</span>
                <Input
                  type="number"
                  min={0}
                  value={weeksCompleteInput}
                  onChange={(e) => setWeeksCompleteInput(e.target.value)}
                  placeholder="—"
                  className="mt-1 w-full"
                  data-testid="timeline-weeks-to-complete"
                />
              </div>
              <div>
                <span className="block text-xs text-foreground-soft">Tentative start date</span>
                <Input
                  type="date"
                  value={tentativeInput}
                  onChange={(e) => setTentativeInput(e.target.value)}
                  className="mt-1 w-full"
                  data-testid="timeline-tentative-start-date"
                />
              </div>
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm"
                disabled={saving}
                onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm"
                disabled={saving}
                className="gap-2 text-xs font-semibold uppercase tracking-wider"
                onClick={() => {
                  void handleSave().then((ok) => {
                    if (ok) setDialogOpen(false);
                  });
                }}>
                {saving ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                  </>
                ) : (
                  'Save timeline'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Parse a date-only string as LOCAL midnight (UTC parsing renders a day off). */
function parseDateOnlyLocal(raw: string | null): Date | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMetricDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * EST-L2 (2026-07-22, approved preview EST-L7) — "Project Detail" as
 * individual `label | selection statement` rows in the same Kohrt-style
 * two-column table Section III uses, under ONE shaded PROJECT DETAIL
 * bar. One row per project-level Scope Notes statement; the row label
 * comes from the matching field's admin-authored scfLabel and a
 * duplicated "Label:" prefix is stripped from the statement text
 * (screenshot defect #4). Replaces the mockup-era cards grid.
 */
function ProjectDetailCards({
  projectScope,
}: {
  projectScope: ProjectScopeBlock | null;
}) {
  const statements = projectScope?.statements ?? [];
  if (statements.length === 0) return null;

  return (
    <div className="border-t border-outline-variant pt-4" data-testid="section-ii-project-detail">
      <ScopeStatementsTable
        statements={statements}
        fields={(projectScope?.fields ?? []).map((f) => ({
          scfCode: f.scfCode,
          scfLabel: f.scfLabel,
          contractLabel: f.contractLabel,
        }))}
        singleBarLabel="PROJECT DETAIL"
        testId="project-detail-table"
      />
    </div>
  );
}

function ProjectNameBlock({
  name,
  editable,
  onSave,
}: {
  name: string;
  editable: boolean;
  onSave?: ProjectNameHandler;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(name);
  }, [name]);

  const display = name.trim() || '—';

  if (!editable || !onSave) {
    return (
      <div data-testid="section-ii-project-name">
        <label className={SECTION_II_LABEL_CLASS}>Project Name</label>
        <p className="mt-1 font-public-sans text-2xl font-semibold text-foreground">
          {display}
        </p>
      </div>
    );
  }

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === name.trim()) {
      setEditing(false);
      setDraft(name);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save project name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="section-ii-project-name">
      <SectionIIHeaderRow
        label="Project Name"
        action={
          !editing ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                SECTION_II_HEADER_ACTION_BUTTON_CLASS,
                'text-primary hover:text-primary-dim',
              )}
              data-testid="section-ii-project-name-edit"
              onClick={() => {
                setDraft(name);
                setEditing(true);
              }}
            >
              Edit
            </Button>
          ) : undefined
        }
      />
      {editing ? (
        <div className="mt-1 space-y-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            data-testid="section-ii-project-name-input"
            className="font-public-sans text-lg font-semibold"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => {
                setEditing(false);
                setDraft(name);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={saving || !draft.trim()}
              data-testid="section-ii-project-name-save"
              onClick={() => void commit()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-1 font-public-sans text-2xl font-semibold text-foreground">
          {display}
        </p>
      )}
    </div>
  );
}

const NARRATIVE_PLACEHOLDER =
  'Write the project narrative, or generate with AI';

/** True when HTML is empty / placeholder-only (no user-visible text). */
export function narrativeHtmlIsEmpty(html: string | null | undefined): boolean {
  if (html == null) return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

function persistedNarrativeHtml(html: string): string | null {
  return narrativeHtmlIsEmpty(html) ? null : html;
}

/**
 * Founder 2026-09-01 — Section II Narrative.
 *
 * Editable estimate: always-visible TipTap box (toolbar on empty),
 * optional Generate with AI, Save when dirty. Empty until the user
 * types or explicitly generates. Generate failure keeps the draft.
 *
 * Read-only modes: HTML body if saved, otherwise omitted.
 */
function NarrativeBlock({
  summary,
  editable,
  onGenerate,
  onSave,
}: {
  summary: string | null;
  editable: boolean;
  onGenerate?: GenerateSummaryHandler;
  onSave?: SummaryOverrideHandler;
}) {
  const [draft, setDraft] = useState(summary ?? '');
  const [lastPersisted, setLastPersisted] = useState(summary ?? '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draftRef = useRef(draft);
  const lastPersistedRef = useRef(lastPersisted);
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    setDraft(summary ?? '');
    setLastPersisted(summary ?? '');
  }, [summary]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  useEffect(() => {
    lastPersistedRef.current = lastPersisted;
  }, [lastPersisted]);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const persistDraft = useCallback(async () => {
    const saver = onSaveRef.current;
    if (!saver) return false;
    const html = persistedNarrativeHtml(draftRef.current);
    const savedHtml = persistedNarrativeHtml(lastPersistedRef.current);
    if (html === savedHtml) return true;
    setSaving(true);
    setError(null);
    try {
      await saver(html);
      setLastPersisted(draftRef.current);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save narrative');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (!editable || !onSave) return;
    const dirty =
      persistedNarrativeHtml(draft) !== persistedNarrativeHtml(lastPersisted);
    if (!dirty) return;
    const handle = window.setTimeout(() => {
      void persistDraft();
    }, 1200);
    return () => window.clearTimeout(handle);
    // persistDraft is stable enough via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, lastPersisted, editable, onSave]);

  useEffect(() => {
    return () => {
      const html = persistedNarrativeHtml(draftRef.current);
      const savedHtml = persistedNarrativeHtml(lastPersistedRef.current);
      if (html !== savedHtml && onSaveRef.current) {
        void onSaveRef.current(html);
      }
    };
  }, []);

  if (!editable) {
    if (narrativeHtmlIsEmpty(summary)) return null;
    return (
      <div data-testid="section-ii-narrative">
        <SectionIIHeaderRow label="Narrative" />
        <div
          className="prose prose-slate mt-1 max-w-none font-newsreader text-base leading-relaxed text-foreground"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(summary)) }}
        />
      </div>
    );
  }

  const dirty =
    persistedNarrativeHtml(draft) !== persistedNarrativeHtml(lastPersisted);
  const generateLabel = narrativeHtmlIsEmpty(draft)
    ? 'Generate with AI'
    : 'Regenerate with AI';

  const handleGenerate = async () => {
    if (!onGenerate || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await onGenerate();
      if (result?.summary != null) {
        setDraft(result.summary);
        setLastPersisted(result.summary);
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to generate narrative',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!onSave || saving) return;
    await persistDraft();
  };

  return (
    <div className="space-y-3" data-testid="section-ii-narrative">
      <SectionIIHeaderRow
        label="Narrative"
        action={
          onGenerate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleGenerate()}
              disabled={generating || saving}
              className={cn(
                SECTION_II_HEADER_ACTION_BUTTON_CLASS,
                'gap-1.5 text-primary hover:text-primary-dim',
              )}
              data-testid="section-ii-generate-summary"
            >
              {generating ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3" /> {generateLabel}
                </>
              )}
            </Button>
          ) : undefined
        }
      />
      <RichTextEditor
        value={draft}
        onChange={setDraft}
        placeholder={NARRATIVE_PLACEHOLDER}
      />
      {error && (
        <p className="text-xs text-red-600" data-testid="section-ii-narrative-error">
          {error}
        </p>
      )}
      {onSave && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving || generating || !dirty}
            data-testid="section-ii-narrative-save"
          >
            {saving ? 'Saving…' : dirty ? 'Save narrative' : 'Saved'}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Sprint 41 W32 enrichment Slice 2 — Section II Assumptions block.
 *
 * Renders:
 *   - editor when `caps.isEditable && onSave` (Edit / Save / Cancel /
 *     Reset-to-default controls + RichTextEditor).
 *   - read-only HTML otherwise. Nothing rendered when assumptions is
 *     null in read-only modes.
 *
 * `onSave(null)` clears the project override; the next page reload
 * shows whatever the admin-layer default supplies (or nothing if no
 * default exists).
 *
 * Same architecture as SectionIV's Contract Terms editor — split into
 * its own sub-component so SectionII stays scannable.
 */
function AssumptionsBlock({
  assumptions,
  isOverride,
  editable,
  onSave,
}: {
  assumptions: string | null;
  isOverride: boolean;
  editable: boolean;
  onSave?: AssumptionsOverrideHandler;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(assumptions ?? '');
  const [busy, setBusy] = useState(false);

  // Read-only modes show the body if set, hide otherwise.
  if (!editable) {
    if (!assumptions) return null;
    return (
      <div>
        <SectionIIHeaderRow label="Assumptions" />
        <div
          className="prose prose-slate mt-1 max-w-none font-newsreader text-base leading-relaxed text-foreground"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(assumptions) }}
        />
      </div>
    );
  }

  const startEditing = () => {
    setDraft(assumptions ?? '');
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
  };
  const save = async () => {
    if (busy || !onSave) return;
    setBusy(true);
    try {
      await onSave(draft.trim() === '' ? null : draft);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    if (busy || !onSave) return;
    if (
      !confirm(
        'Reset assumptions to the admin-layer default? This clears the project override.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await onSave(null);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-section-block="assumptions">
      <SectionIIHeaderRow
        label="Assumptions"
        action={
          !editing ? (
            <>
              {isOverride && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={reset}
                  disabled={busy}
                  className={cn(
                    SECTION_II_HEADER_ACTION_BUTTON_CLASS,
                    'text-foreground-soft hover:text-destructive',
                  )}
                >
                  Reset to default
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={startEditing}
                disabled={busy}
                className={cn(
                  SECTION_II_HEADER_ACTION_BUTTON_CLASS,
                  'text-primary hover:text-primary-dim',
                )}
              >
                {isOverride ? 'Edit override' : 'Edit'}
              </Button>
            </>
          ) : undefined
        }
      />
      {editing ? (
        <div className="space-y-3">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder="Project-level assumptions…"
          />
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm"
              onClick={cancel}
              disabled={busy}
              className="text-foreground-soft hover:bg-surface-low hover:text-foreground disabled:opacity-50">
              Cancel
            </Button>
            <Button type="button" size="sm"
              onClick={() => void save()}
              disabled={busy || draft === (assumptions ?? '')}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : assumptions ? (
        <>
          <div
            className="prose prose-slate max-w-none font-newsreader text-base leading-relaxed text-foreground"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(assumptions) }}
          />
          {isOverride ? (
          <p className="text-xs italic text-muted-foreground">
            This project overrides the Document Language default.
          </p>
          ) : (
          <p className="text-xs italic text-muted-foreground">
            From Document Language → Contract Assumptions.
          </p>
          )}
        </>
      ) : (
        <p className="text-sm italic text-muted-foreground">
          No assumptions in Document Language. Click <strong>Edit</strong> to add a project-level override.
        </p>
      )}
    </div>
  );
}

function DetailGroup({
  label,
  items,
  editable = false,
  onSave,
  showCosts = false,
  costsEditable = false,
  onDetailsMutated,
  onLineItemDelete,
}: {
  label: string;
  items: ProjectDetail[];
  editable?: boolean;
  onSave?: DetailsTableHandler;
  showCosts?: boolean;
  costsEditable?: boolean;
  onDetailsMutated?: DetailsMutatedHandler;
  onLineItemDelete?: LineItemDeleteHandler;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-outline-variant bg-surface-low">
      <Button type="button" variant="ghost"
        className="flex h-auto w-full items-center justify-between gap-2 rounded-none px-4 py-3 text-left font-normal hover:bg-surface-low hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="font-public-sans text-sm font-semibold text-foreground">
          {label}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </span>
      </Button>
      {open && (
        <div className="space-y-3 border-t border-outline-variant bg-card px-4 py-3">
          {items.map((d) => (
            <DetailRow
              key={d.id}
              detail={d}
              editable={editable && !!onSave}
              onSave={onSave}
              showCosts={showCosts}
              costsEditable={costsEditable}
              onDetailsMutated={onDetailsMutated}
              onLineItemDelete={onLineItemDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Wave 4e — Slice 4 DetailsTable inline edit row.
 *
 * Read-only when `editable=false`. When editable, clicking the value
 * swaps to an input + Save/Cancel. Value type is inferred from the
 * current value (boolean → checkbox, number → number input, else
 * string). Per the Q1 design lock-in only the value is editable;
 * label and key come from the admin layer.
 */
function DetailRow({
  detail,
  editable,
  onSave,
  showCosts = false,
  costsEditable = false,
  onDetailsMutated,
  onLineItemDelete,
  allowOther = false,
}: {
  detail: ProjectDetail;
  editable: boolean;
  onSave?: DetailsTableHandler;
  showCosts?: boolean;
  costsEditable?: boolean;
  onDetailsMutated?: DetailsMutatedHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  allowOther?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(formatDetailValue(detail.value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const allowsCosts = detail.allowsLineItems !== false;
  const lineItems = detail.lineItems ?? [];
  const showCostBlock = showCosts && allowsCosts;
  const [addingSku, setAddingSku] = useState<
    'MATERIAL' | 'SERVICE' | 'OTHER' | null
  >(null);
  const [addSkuError, setAddSkuError] = useState<string | null>(null);

  const inferredType: 'boolean' | 'number' | 'string' =
    typeof detail.value === 'boolean'
      ? 'boolean'
      : typeof detail.value === 'number'
      ? 'number'
      : 'string';

  const startEdit = () => {
    setDraft(formatDetailValue(detail.value));
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const commit = async () => {
    if (!onSave) return;
    setSaving(true);
    setError(null);
    let coerced: unknown = draft;
    if (inferredType === 'number') {
      const n = parseFloat(draft);
      if (Number.isNaN(n)) {
        setError('Not a valid number');
        setSaving(false);
        return;
      }
      coerced = n;
    } else if (inferredType === 'boolean') {
      const lower = draft.trim().toLowerCase();
      if (lower !== 'true' && lower !== 'false') {
        setError('Expected true or false');
        setSaving(false);
        return;
      }
      coerced = lower === 'true';
    } else {
      coerced = draft === '' ? null : draft;
    }
    try {
      await onSave({ detailId: detail.id, value: coerced });
      setEditing(false);
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="space-y-2 border-b border-outline-variant/60 py-2 last:border-b-0"
      data-detail-key={detail.key}
      data-testid={`project-detail-row-${detail.key}`}
    >
      {/* Reserved Administration row is a cost container — no value editor. */}
      {detail.key !== 'administration' && (
      <div className="grid grid-cols-[minmax(0,38%)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-1">
        <span className="font-public-sans text-sm text-foreground-soft">
          {formatPaperFieldLabel(
            displayProjectDetailLabel(detail),
            formatDetailValue(detail.value),
          )}
        </span>
        {!editing ? (
          <span className="flex items-baseline gap-2">
            <span className="font-public-sans text-sm text-foreground">
              {typeof detail.value === 'string'
                ? formatAnswerDisplay(
                    formatPaperFieldValue(
                      displayProjectDetailLabel(detail),
                      formatDetailValue(detail.value),
                    ),
                  )
                : formatDetailValue(detail.value)}
            </span>
            {editable && (
              <Button type="button" variant="ghost" size="sm"
                onClick={startEdit}
                className="h-auto px-1 py-0 text-xs font-semibold text-primary hover:text-primary-dim">
                Edit
              </Button>
            )}
          </span>
        ) : (
          <span className="flex flex-col items-start gap-1">
            <span className="flex items-center gap-1">
              <Input
                type={inferredType === 'number' ? 'number' : 'text'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
                className="h-8 rounded border border-outline-variant px-2 py-1 text-sm"
                placeholder={inferredType === 'boolean' ? 'true / false' : ''}
              />
              <Button type="button" variant="ghost" size="sm"
                onClick={commit}
                disabled={saving}
                className="h-auto px-1 py-0 text-xs font-semibold text-primary hover:text-primary-dim disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button type="button" variant="ghost" size="sm"
                onClick={cancel}
                disabled={saving}
                className="h-auto px-1 py-0 text-xs text-muted-foreground hover:text-foreground">
                Cancel
              </Button>
            </span>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </span>
        )}
      </div>
      )}

      {/* Founder pick 2026-08-04 (bug 3, option B) — the boxed empty state
          ("No material or service costs yet.") is gone. With no costs the row
          renders just the add affordance, unboxed; the box chrome returns
          once there is something to hold. */}
      {showCostBlock && (lineItems.length > 0 || (costsEditable && onDetailsMutated)) && (
        <div
          className={
            lineItems.length > 0
              ? 'rounded-md border border-outline-variant/80 bg-muted/20 px-3 py-2'
              : undefined
          }
          data-testid={`project-detail-costs-${detail.key}`}
        >
          {lineItems.length > 0 && (
            <ul className="space-y-1.5">
              {lineItems.map((li) => {
                const canDelete =
                  costsEditable &&
                  onLineItemDelete != null &&
                  li.isLocked !== true;
                return (
                  <li
                    key={li.id}
                    className="flex flex-wrap items-baseline justify-between gap-2 font-public-sans text-xs text-foreground"
                    data-testid={`project-detail-line-item-${li.id}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="mr-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                        {li.type === 'SERVICE'
                          ? 'Service'
                          : li.type === 'OTHER'
                            ? 'Other'
                            : 'Material'}
                      </span>
                      {li.purpose ? `${li.purpose} — ` : ''}
                      {li.description}
                      <span className="ml-1 text-muted-foreground">
                        × {li.quantity} {li.unit}
                      </span>
                    </span>
                    <span className="flex items-baseline gap-2">
                      <span className="tabular-nums font-semibold">
                        {formatCurrency(li.lineTotal)}
                      </span>
                      {canDelete && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Delete ${
                            li.type === 'SERVICE'
                              ? 'service'
                              : li.type === 'OTHER'
                                ? 'other'
                                : 'material'
                          } cost ${li.description}`}
                          disabled={deletingId === li.id}
                          className="h-auto px-1 py-0 text-muted-foreground hover:text-destructive"
                          onClick={async () => {
                            if (
                              !confirm(
                                `Delete ${
                                  li.type === 'SERVICE'
                                    ? 'service'
                                    : li.type === 'OTHER'
                                      ? 'other'
                                      : 'material'
                                } cost "${li.description}"?`,
                              )
                            ) {
                              return;
                            }
                            setDeleteError(null);
                            setDeletingId(li.id);
                            try {
                              // Handler reloads resolve (EstimateView.load).
                              await onLineItemDelete!(li.id);
                            } catch (err: unknown) {
                              setDeleteError(
                                err instanceof Error
                                  ? err.message
                                  : 'Delete failed',
                              );
                            } finally {
                              setDeletingId(null);
                            }
                          }}
                        >
                          ×
                        </Button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {deleteError && (
            <p className="mt-1 text-xs text-red-600">{deleteError}</p>
          )}
          {lineItems.length > 0 && detail.rowTotal != null && (
            <p className="mt-2 flex justify-end border-t border-outline-variant/60 pt-1.5 font-public-sans text-xs font-semibold text-foreground">
              Row total{' '}
              <span className="ml-2 tabular-nums">
                {formatCurrency(detail.rowTotal)}
              </span>
            </p>
          )}
          {costsEditable && onDetailsMutated && (
            <div className="mt-2" data-testid={`project-detail-add-sku-${detail.key}`}>
              {addingSku === 'OTHER' ? (
                <>
                  <AddOtherInline
                    onCancel={() => {
                      setAddingSku(null);
                      setAddSkuError(null);
                    }}
                    onCommit={async (payload) => {
                      try {
                        setAddSkuError(null);
                        await estimateApi.createDetailLineItem(detail.id, {
                          type: 'OTHER',
                          description: payload.description,
                          quantity: payload.quantity,
                          unit: 'EA',
                          purpose: payload.purpose,
                          initialCost: {
                            productMsku: null,
                            serviceId: null,
                            description: payload.description,
                            unitCostSnapshot: payload.unitCost,
                          },
                        });
                        setAddingSku(null);
                        await onDetailsMutated();
                      } catch (err: unknown) {
                        const message =
                          err instanceof Error ? err.message : 'Save failed';
                        setAddSkuError(message);
                      }
                    }}
                  />
                  {addSkuError && (
                    <p className="mt-1 text-xs text-red-600">{addSkuError}</p>
                  )}
                </>
              ) : addingSku ? (
                <>
                  <AddSkuInline
                    type={addingSku}
                    onCancel={() => {
                      setAddingSku(null);
                      setAddSkuError(null);
                    }}
                    onCommit={async (payload) => {
                      const sku = payload.sku.trim() || null;
                      try {
                        setAddSkuError(null);
                        await estimateApi.createDetailLineItem(detail.id, {
                          type: addingSku,
                          description: payload.description,
                          quantity: payload.quantity,
                          unit: 'EA',
                          purpose: payload.purpose,
                          initialCost: {
                            productMsku:
                              addingSku === 'MATERIAL' ? sku : null,
                            serviceId: addingSku === 'SERVICE' ? sku : null,
                            description: payload.description,
                            unitCostSnapshot: payload.unitCost,
                          },
                        });
                        setAddingSku(null);
                        await onDetailsMutated();
                      } catch (err: unknown) {
                        const message =
                          err instanceof Error ? err.message : 'Save failed';
                        setAddSkuError(message);
                      }
                    }}
                  />
                  {addSkuError && (
                    <p className="mt-1 text-xs text-red-600">{addSkuError}</p>
                  )}
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
                    onClick={() => {
                      setAddSkuError(null);
                      setAddingSku('MATERIAL');
                    }}
                  >
                    + Add material
                  </Button>
                  <span
                    className="text-xs text-muted-foreground"
                    aria-hidden="true"
                  >
                    ·
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
                    onClick={() => {
                      setAddSkuError(null);
                      setAddingSku('SERVICE');
                    }}
                  >
                    + Add service
                  </Button>
                  {allowOther && (
                    <>
                      <span
                        className="text-xs text-muted-foreground"
                        aria-hidden="true"
                      >
                        ·
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
                        onClick={() => {
                          setAddSkuError(null);
                          setAddingSku('OTHER');
                        }}
                      >
                        + Add Other
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function toNumberOrNull(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toDateInputValue(v: unknown): string {
  if (!v) return '';
  // Date-only strings must stay calendar-stable — `new Date('YYYY-MM-DD')`
  // is UTC midnight and shifts a day in local TZ (same defect as the
  // metrics band, which uses parseDateOnlyLocal).
  if (typeof v === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(v.trim());
    if (m) return m[1];
  }
  // Accept Date objects or full ISO timestamps.
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return '';
  // <input type="date"> wants YYYY-MM-DD in local time.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}


function displayProjectDetailLabel(detail: { key?: string; label: string }): string {
  if (detail.key === 'demolition:required') return 'Demolition Required';
  return detail.label;
}

function formatDetailValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((v) => formatDetailValue(v)).join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('value' in obj) return formatDetailValue(obj.value);
    return JSON.stringify(value);
  }
  return String(value);
}
